/**
 * SelectionCounter Component
 * 
 * Displays current selection count vs maximum allowed.
 * Visual states: normal, warning (>=80%), error (>=100%)
 * Shows batch progress when items have been processed.
 * 
 * @implements REQ-1.2: Selection Counter UI Component
 * @since 2026-03-04 (Phase 1: Limiting Bulk Actions)
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * REQ-1.2: Selection counter chip showing selected/max format
 * 
 * @param {Object} props
 * @param {number} props.selected - Current number of selected items
 * @param {number} props.max - Maximum selectable items (default 500)
 * @param {number} [props.processedCount] - Number of already-processed items (batch sequential)
 * @param {Object} [props.messages] - i18n messages object
 * @returns {JSX.Element}
 */
const SelectionCounter = React.memo(({ selected, max, processedCount = 0, messages }) => {
    // REQ-1.2: Calculate percentage for visual states
    const percentage = max > 0 ? (selected / max) * 100 : 0;
    const isNearLimit = percentage >= 80;
    const isAtLimit = percentage >= 100;

    /**
     * REQ-1.2: Visual states
     * - Normal: Default styling (default chip color)
     * - Near limit (>=80%): Warning color (yellow/amber)
     * - At limit (100%): Error color (red) + icon indicator
     */
    const getChipColor = () => {
        if (isAtLimit) return 'error';
        if (isNearLimit) return 'warning';
        return 'default';
    };

    // REQ-1.2: Tooltip message when at limit - uses i18n message with {max} placeholder
    const tooltipMessage = isAtLimit 
        ? (messages?.selectionLimitTooltip?.replace('{max}', max) || `Cannot select more items. Maximum ${max} items allowed.`)
        : '';

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Tooltip title={tooltipMessage} placement="top">
                <Chip
                    size="small"
                    color={getChipColor()}
                    icon={isAtLimit ? <WarningAmberIcon fontSize="small" /> : undefined}
                    label={`${selected} / ${max}`}
                    sx={{
                        minWidth: 70,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        // REQ-1.2: At limit state - enhanced visibility
                        ...(isAtLimit && {
                            '& .MuiChip-icon': {
                                color: 'inherit',
                            },
                        }),
                    }}
                />
            </Tooltip>
            {/* Batch sequential: show processed count */}
            {processedCount > 0 && (
                <Tooltip title={messages?.batchProcessedTooltip?.replace('{count}', processedCount) || `${processedCount} items already processed in this batch session`} placement="top">
                    <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleOutlineIcon fontSize="small" />}
                        label={`${processedCount}`}
                        variant="outlined"
                        sx={{
                            minWidth: 50,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                        }}
                    />
                </Tooltip>
            )}
        </div>
    );
});

SelectionCounter.displayName = 'SelectionCounter';

export default SelectionCounter;
